import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { renderSaleCard, canvasToBlob } from "@/lib/saleCard";
import { Loader2, Share2, Link2, ImageDown, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const BASE_TEXT = "I just copped an exclusive Co Mann beat 🔥";

export const SaleCelebration = ({ track }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [handle, setHandle] = useState("");
  const [onCard, setOnCard] = useState(null);
  const storeUrl = window.location.origin;
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const track_ = (action) => { if (sessionId) api.post("/share-events", { session_id: sessionId, action }).catch(() => {}); };
  const cardOpts = { handle: onCard ? handle : "" };
  const shareText = handle ? `${BASE_TEXT} Tag @${handle}` : BASE_TEXT;

  useEffect(() => {
    api.get("/settings/public")
      .then((r) => { setHandle(r.data.instagram_handle || ""); setOnCard(!!r.data.card_show_handle); })
      .catch(() => setOnCard(false));
  }, []);

  useEffect(() => {
    if (onCard === null) return undefined;
    let alive = true;
    renderSaleCard(track, "square", cardOpts).then((c) => {
      try { if (alive) setPreview(c.toDataURL("image/png")); } catch { if (alive) setPreview("error"); }
    });
    return () => { alive = false; };
  }, [track, onCard, handle]); // eslint-disable-line react-hooks/exhaustive-deps

  const fileFor = async (kind) => {
    const blob = await canvasToBlob(await renderSaleCard(track, kind, cardOpts));
    return new File([blob], `co-mann-${kind}-${track.title.replace(/[^\w-]+/g, "_")}.png`, { type: "image/png" });
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const f = await fileFor(kind);
      const url = URL.createObjectURL(f);
      const a = document.createElement("a"); a.href = url; a.download = f.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      track_(kind);
      toast.success(kind === "story" ? "Story image saved — post it to your IG story!" : "Square image saved — ready for your feed!");
    } catch { toast.error("Couldn't create the image. Please try again."); }
    finally { setBusy(null); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(storeUrl); toast.success("Store link copied"); track_("copy"); }
    catch { toast.error(`Copy this link: ${storeUrl}`); }
  };

  const share = async () => {
    setBusy("share");
    try {
      const f = await fileFor("square");
      if (navigator.canShare?.({ files: [f] })) { await navigator.share({ files: [f], text: `${shareText} ${storeUrl}` }); track_("share"); }
      else if (navigator.share) { await navigator.share({ title: "Co Mann Beats", text: shareText, url: storeUrl }); track_("share"); }
      else await copy();
    } catch (e) { if (e?.name !== "AbortError") await copy(); }
    finally { setBusy(null); }
  };

  const btn = "flex items-center justify-center gap-2 rounded-full border border-[#2a2a2a] hover:border-[#e2ff31]/60 hover:text-[#e2ff31] text-sm font-semibold py-2.5 transition-colors disabled:opacity-60";
  return (
    <div data-testid="sale-celebration" className="mt-8 pt-6 border-t border-[#2a2a2a] text-left">
      <p className="font-heading font-semibold flex items-center gap-2"><PartyPopper className="w-4 h-4 text-[#e2ff31]" /> Flex your new exclusive</p>
      <p className="text-xs text-[#6b7280] mt-1 mb-4">
        Share it on Instagram{handle ? <> and tag <span data-testid="sale-card-handle" className="text-[#e2ff31]">@{handle}</span></> : " and tag the producer"}.
      </p>
      <div className="aspect-square rounded-xl overflow-hidden border border-[#2a2a2a] bg-[#0a0a0a] flex items-center justify-center">
        {!preview && <Loader2 className="w-6 h-6 animate-spin text-[#e2ff31]" />}
        {preview && preview !== "error" && <img data-testid="sale-card-preview" src={preview} alt="Share card preview" className="w-full h-full object-cover" />}
        {preview === "error" && <p className="text-xs text-[#6b7280] px-6 text-center">Preview unavailable — downloads still work.</p>}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button data-testid="sale-card-download-story" onClick={() => download("story")} disabled={!!busy} className={btn}>
          {busy === "story" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />} Story 9:16
        </button>
        <button data-testid="sale-card-download-square" onClick={() => download("square")} disabled={!!busy} className={btn}>
          {busy === "square" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />} Square 1:1
        </button>
        <button data-testid="sale-card-share" onClick={share} disabled={!!busy} className={btn}>
          {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
        </button>
        <button data-testid="sale-card-copy-link" onClick={copy} className={btn}><Link2 className="w-4 h-4" /> Copy link</button>
      </div>
    </div>
  );
};
